/*
 * Which of the appliance's proposals and drawings belong to which call in a
 * conversation.
 *
 * Both stores are edge-wide: they hold entries raised by other conversations,
 * by other tabs, and before this window last loaded. So a conversation keeps
 * its own scope, stored with it: what already existed when it began (never
 * its own), and what it has claimed since (always its own). Coming back to a
 * conversation restores that scope instead of re-deriving it from whatever
 * exists at the moment the window mounts, which is what made a conversation's
 * own cards vanish the first time the operator stepped over to the console and
 * back.
 */

/* Key order is not meaningful and the two sides do not agree on it: the stored
   body is re-encoded from a map, which sorts, while the transcript carries the
   call exactly as it was written. Compare canonically or an identical change
   fails to match itself and its card silently becomes a bare tool row. */
const canon = (v) => {
  if (Array.isArray(v)) return v.map(canon);
  if (v && typeof v === "object") {
    return Object.keys(v).sort().reduce((o, k) => { o[k] = canon(v[k]); return o; }, {});
  }
  return v;
};

const sameArgs = (a, b) => {
  try { return JSON.stringify(canon(a ?? {})) === JSON.stringify(canon(b ?? {})); }
  catch { return false; }
};

// Artifacts are matched to the tool call that produced them by CONTENT, never
// by position: counting "the Nth propose_ call is the Nth proposal" silently
// wires a card to the wrong change, which is how an Apply button ends up
// applying something else.
export function matchProposal(proposals, msg, used) {
  return proposals.find(
    (p) => !used.has(p.id) && p.tool === msg.name && sameArgs(p.body, msg.input)
  );
}

/* What the appliance files a drawing tool's output under. A call is never
   matched to a drawing of another kind, even one with the same title. */
const drawingKind = (tool) =>
  tool.startsWith("draw_") ? tool.slice("draw_".length)
    : tool === "compose_answer" ? "answer"
    : tool.startsWith("guide_") ? "tour"
    : "";

export const isDrawingCall = (m) =>
  m.role === "tool" && !!m.name &&
  (m.name.startsWith("draw_") || m.name.startsWith("compose_") || m.name.startsWith("guide_"));

export const isProposalCall = (m) =>
  m.role === "tool" && !!m.name && m.name.startsWith("propose_");

export function matchArtifact(artifacts, msg, used) {
  const title = msg.input && msg.input.title;
  const kind = drawingKind(msg.name || "");
  return artifacts.find(
    (a) => !used.has(a.id) &&
      (!kind || !a.kind || a.kind === kind) &&
      (title ? a.title === title : true)
  );
}

/* A conversation's scope. `prior` is null until the snapshot is known: until
   then nothing unclaimed is matched, so an older entry never flashes up as
   this conversation's while the lists load. */
export const emptyScope = () => ({
  prior: { proposals: null, drawings: null },
  owns: { proposals: new Set(), drawings: new Set() },
});

const idsOf = (list) => new Set((list || []).map((x) => x.id));

/** beginScope is the scope of a conversation starting now: everything the
    appliance already holds is someone else's. */
export const beginScope = (proposals, drawings) => ({
  prior: { proposals: idsOf(proposals), drawings: idsOf(drawings) },
  owns: { proposals: new Set(), drawings: new Set() },
});

/** withPrior fills in whichever half of the snapshot is still unknown, so a
    conversation resumed with a stored scope keeps the one it began with. */
export const withPrior = (scope, key, list) =>
  scope.prior[key] ? scope : { ...scope, prior: { ...scope.prior, [key]: idsOf(list) } };

/** storedScope is the scope as kept with the conversation's record. */
export const storedScope = (scope) => ({
  prior: {
    proposals: scope.prior.proposals ? [...scope.prior.proposals] : null,
    drawings: scope.prior.drawings ? [...scope.prior.drawings] : null,
  },
  owns: { proposals: [...scope.owns.proposals], drawings: [...scope.owns.drawings] },
});

/** restoreScope reads a stored scope back; one kept by an older build, or none
    at all, starts like a new conversation and learns its snapshot on load. */
export const restoreScope = (stored) => {
  if (!stored || !stored.prior || !stored.owns) return emptyScope();
  const set = (v) => (Array.isArray(v) ? new Set(v) : null);
  return {
    prior: { proposals: set(stored.prior.proposals), drawings: set(stored.prior.drawings) },
    owns: { proposals: set(stored.owns.proposals) || new Set(), drawings: set(stored.owns.drawings) || new Set() },
  };
};

/** ownedElsewhere is everything other conversations on this edge have claimed,
    which this one must never take for its own calls. */
export function ownedElsewhere(records, conversationId) {
  const out = { proposals: new Set(), drawings: new Set() };
  (records || []).forEach((r) => {
    if (r.id === conversationId || !r.scope?.owns) return;
    (r.scope.owns.proposals || []).forEach((id) => out.proposals.add(id));
    (r.scope.owns.drawings || []).forEach((id) => out.drawings.add(id));
  });
  return out;
}

/* The candidates a conversation may match, its own first: a call that has
   been claimed finds its artifact again before anything newer that happens to
   look the same. */
const pool = (list, owned, prior, elsewhere) => {
  const own = list.filter((x) => owned.has(x.id));
  const open = prior
    ? list.filter((x) => !owned.has(x.id) && !prior.has(x.id) && !elsewhere.has(x.id))
    : [];
  return [...own, ...open];
};

/**
 * assignArtifacts decides, once per render, which artifact each call in the
 * transcript shows. The transcript draws from it and the window claims from
 * it, so what is shown and what is remembered can never disagree.
 */
export function assignArtifacts(messages, proposals, drawings, scope, elsewhere) {
  const none = { proposals: new Set(), drawings: new Set() };
  const away = elsewhere || none;
  const props = pool(proposals || [], scope.owns.proposals, scope.prior.proposals, away.proposals);
  const draws = pool(drawings || [], scope.owns.drawings, scope.prior.drawings, away.drawings);
  const at = new Map();
  const used = new Set();
  const out = { at, proposals: new Set(), drawings: new Set() };
  (messages || []).forEach((m, i) => {
    if (isDrawingCall(m)) {
      const a = matchArtifact(draws, m, used);
      if (a) { used.add(a.id); at.set(i, a); out.drawings.add(a.id); }
    } else if (isProposalCall(m)) {
      const p = matchProposal(props, m, used);
      if (p) { used.add(p.id); at.set(i, p); out.proposals.add(p.id); }
    }
  });
  return out;
}

/** claim adds what the transcript now shows to what the conversation owns,
    returning the same scope when there is nothing new. */
export function claim(scope, assigned) {
  const fresh = (owned, ids) => [...ids].filter((id) => !owned.has(id));
  const p = fresh(scope.owns.proposals, assigned.proposals);
  const d = fresh(scope.owns.drawings, assigned.drawings);
  if (!p.length && !d.length) return scope;
  return {
    ...scope,
    owns: {
      proposals: new Set([...scope.owns.proposals, ...p]),
      drawings: new Set([...scope.owns.drawings, ...d]),
    },
  };
}

/* Every pending change must be reachable, always. Whatever the transcript
   renders inline is accounted for; anything left — raised in a conversation
   that has since been closed, or one this browser never saw — surfaces after
   the transcript instead of existing only on the appliance where nobody can
   act on it. */
export function orphanPending(pending, assigned) {
  return pending.filter((p) => !assigned.proposals.has(p.id));
}
